import { Component } from 'react';
import type { ReactNode } from 'react';
import { captureError } from '@/services/monitoring/sentry';

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)

    // Detect chunk/module loading failures (stale deployment cache)
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError()
      return
    }

    // In production, send to Sentry error tracking
    if (import.meta.env.PROD) {
      captureError(error, {
        component: 'ErrorBoundary',
        action: errorInfo.componentStack || 'unknown',
      })
    }
  }

  private isChunkLoadError(error: Error): boolean {
    const message = error.message || ''
    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk') ||
      message.includes('dynamically imported module')
    )
  }

  private handleChunkLoadError() {
    const RELOAD_KEY = 'chunk-reload-ts'
    const lastReload = sessionStorage.getItem(RELOAD_KEY)
    const now = Date.now()

    // Only auto-reload if we haven't done so in the last 10 seconds (prevent loops)
    if (!lastReload || now - Number(lastReload) > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(now))
      window.location.reload()
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false })
  }

  private handleReload = () => {
    window.location.reload()
  }

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Terjadi Kesalahan
            </h2>

            <p className="text-gray-600 mb-6">
              Maaf, terjadi kesalahan yang tidak terduga. Silakan coba muat ulang halaman.
            </p>
            
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-3 bg-red-50 rounded-md text-left">
                <p className="text-sm text-red-800 font-mono">
                  {this.state.error.message}
                </p>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Coba Lagi
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Muat Ulang
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}